import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  CheckoutOptimizationStep,
  RcoSegmentStep,
  rcoSegmentStepSchema,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-model/advanced-flow/common";
import {
  AddressPropertiesSegmentSchema,
  CartValueSegmentSchema,
  CheckoutProbabilitySegmentSchema,
  CrossBorderShipmentSegmentSchema,
  CustomerReturnRateSegmentSchema,
  CustomerTagsSegmentSchema,
  DeliveryDistanceMilesSegmentSchema,
  DeliveryLocationNarySegment,
  DeliveryLocationNarySegmentSchema,
  DeliveryLocationSegmentSchema,
  HistoricalOrderValueSegmentSchema,
  ItemQuantitySegmentSchema,
  LineItemPropertiesSegmentSchema,
  OrderCountSegmentSchema,
  OriginLocationSegmentSchema,
  PoBoxShipmentSegmentSchema,
  ProductCollectionsSegmentSchema,
  ProductMetafieldsSegmentSchema,
  ProductSkuSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
  SegmentableCheckoutCharacteristic,
  ShipmentWeightSegmentSchema,
  SUBSCRIPTIONS_MODE_FORBIDDEN_SEGMENTS,
  TimeOfDaySegmentSchema,
} from "@redotech/redo-model/checkouts/checkout-tree";
import {
  getDefaultNarySegment,
  getDefaultSegments,
  NarySegmentCharacteristic,
  narySegmentCharacteristics,
  segmentableCharacteristicLabel,
} from "@redotech/redo-model/checkouts/segments";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { ObjectId } from "bson";
import { memo, useMemo } from "react";
import * as z from "zod/v4";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { FlowInProgress, NodeSelectionMode } from "../../flow-editor";
import { deleteElementAndAllChildren } from "../../utils/delete-element";
import { copySubtree } from "../../utils/selection-mode";
import {
  AddressPropertiesInspectorContent,
  EditableAddressPropertiesInspectorContent,
} from "./address-properties/address-properties-inspector";
import {
  CartValueInspectorContent,
  EditableCartValueInspectorContent,
} from "./cart-value/cart-value-inspector";
import {
  CheckoutProbabilityInspectorContent,
  EditableCheckoutProbabilityInspectorContent,
} from "./checkout-probability/checkout-probability-inspector";
import {
  CrossBorderShipmentInspectorContent,
  EditableCrossBorderShipmentInspectorContent,
} from "./cross-border-shipment/cross-border-shipment-inspector";
import {
  CustomerOrderCountInspectorContent,
  EditableCustomerOrderCountInspectorContent,
} from "./customer-order-count/customer-order-count-inspector";
import {
  CustomerReturnRateInspectorContent,
  EditableCustomerReturnRateInspectorContent,
} from "./customer-return-rate/customer-return-rate-inspector";
import {
  CustomerTagsInspectorContent,
  EditableCustomerTagsInspectorContent,
} from "./customer-tags/customer-tags-inspector";
import {
  DatetimeRangeInspectorContent,
  EditableDatetimeRangeInspectorContent,
} from "./datetime-range/datetime-range-inspector";
import {
  DeliveryDistanceMilesInspectorContent,
  EditableDeliveryDistanceMilesInspectorContent,
} from "./delivery-distance-miles/delivery-distance-miles-inspector";
import {
  DeliveryLocationInspectorContent,
  EditableDeliveryLocationInspectorContent,
} from "./delivery-location/delivery-location-inspector";
import {
  DeliveryLocationNaryInspectorContent,
  EditableDeliveryLocationNaryInspectorContent,
} from "./delivery-location/delivery-location-nary-inspector";
import {
  EditableHistoricalOrderValueInspectorContent,
  HistoricalOrderValueInspectorContent,
} from "./historical-order-value/historical-order-value-inspector";
import {
  EditableItemQuantityInspectorContent,
  ItemQuantityInspectorContent,
} from "./item-quantity/item-quantity-inspector";
import {
  EditableLineItemPropertiesInspectorContent,
  LineItemPropertiesInspectorContent,
} from "./line-item-properties/line-item-properties-inspector";
import {
  EditableOriginLocationInspectorContent,
  OriginLocationInspectorContent,
} from "./origin-location/origin-location-inspector";
import {
  EditablePoBoxShipmentInspectorContent,
  PoBoxShipmentInspectorContent,
} from "./po-box-shipment/po-box-shipment-inspector";
import {
  EditableProductCollectionsInspectorContent,
  ProductCollectionsInspectorContent,
} from "./product-collections/product-collections-inspector";
import {
  EditableProductMetafieldsInspectorContent,
  ProductMetafieldsInspectorContent,
} from "./product-metafields/product-metafields-inspector";
import {
  EditableProductSkuInspectorContent,
  ProductSkuInspectorContent,
} from "./product-sku/product-sku-inspector";
import {
  EditableProductTagsInspectorContent,
  ProductTagsInspectorContent,
} from "./product-tags/product-tags-inspector";
import {
  EditableProductVendorInspectorContent,
  ProductVendorInspectorContent,
} from "./product-vendor/product-vendor-inspector";
import {
  EditableShipmentWeightInspectorContent,
  ShipmentWeightInspectorContent,
} from "./shipment-weight/shipment-weight-inspector";
import {
  EditableTimeOfDayInspectorContent,
  TimeOfDayInspectorContent,
} from "./time-of-day/time-of-day-inspector";

const rcoSegmentStepSchemaOptionalNextIds = rcoSegmentStepSchema.partial({
  matchingNextId: true,
  nonMatchingNextId: true,
});
export type RcoSegmentStepOptionalNextIds = z.infer<
  typeof rcoSegmentStepSchemaOptionalNextIds
>;

function RcoSegmentInspector({
  step,
  setStep,
  readonly,
  steps,
  selectionMode,
  setAutomation,
  onClose,
}: StepTypeDetailsProps<PartialWithRequiredType<RcoSegmentStep>>) {
  const team = useRequiredContext(TeamContext);

  const handleCharacteristicSelected = useHandler(
    ({
      characteristic,
      step,
    }: {
      characteristic: SegmentableCheckoutCharacteristic;
      step: PartialWithRequiredType<RcoSegmentStep>;
    }) => {
      if (narySegmentCharacteristics.has(characteristic)) {
        const defaultEdgeId = new ObjectId().toString();
        const edges: { edgeId: string; nextId: string }[] = [];
        const edgeIds: string[] = [];

        // Migrate nonMatchingNextId as a condition edge
        if (step.nonMatchingNextId) {
          const conditionEdgeId = new ObjectId().toString();
          edgeIds.push(conditionEdgeId);
          edges.push({
            edgeId: conditionEdgeId,
            nextId: step.nonMatchingNextId,
          });
        }

        // Always ensure at least one condition edge exists
        if (edgeIds.length === 0) {
          const conditionEdgeId = new ObjectId().toString();
          edgeIds.push(conditionEdgeId);
          edges.push({ edgeId: conditionEdgeId, nextId: "" });
        }

        // Migrate matchingNextId as the default edge
        edges.push({
          edgeId: defaultEdgeId,
          nextId: step.matchingNextId ?? "",
        });

        const newStep: PartialWithRequiredType<RcoSegmentStep> = {
          ...step,
          segment: getDefaultNarySegment(characteristic, edgeIds),
          edges,
          defaultEdgeId,
          matchingNextId: undefined,
          nonMatchingNextId: undefined,
        };
        setStep(newStep);
      } else {
        // Migrate n-ary to binary:
        // - default edge → nonMatchingNextId
        // - first condition edge → matchingNextId
        // - all other condition edges → recursively delete subtrees
        const defaultEdge = step.edges?.find(
          (e) => e.edgeId === step.defaultEdgeId,
        );
        const conditionEdges = (step.edges ?? []).filter(
          (e) => e.edgeId !== step.defaultEdgeId,
        );
        const firstConditionEdge = conditionEdges[0];
        const excessEdges = conditionEdges.slice(1);

        // Delete subtrees of excess condition edges
        if (setAutomation && excessEdges.length > 0) {
          setAutomation((prev) => {
            if (!prev) {
              return prev;
            }
            let updatedSteps = [...prev.steps];
            for (const edge of excessEdges) {
              if (edge.nextId) {
                updatedSteps = deleteElementAndAllChildren(
                  edge.nextId,
                  updatedSteps,
                );
              }
            }
            return { ...prev, steps: updatedSteps };
          });
        }

        const newStep: PartialWithRequiredType<RcoSegmentStep> = {
          ...step,
          segment:
            getDefaultSegments(team)[
              characteristic as Exclude<
                SegmentableCheckoutCharacteristic,
                NarySegmentCharacteristic
              >
            ],
          edges: undefined,
          defaultEdgeId: undefined,
          matchingNextId: firstConditionEdge?.nextId ?? step.matchingNextId,
          nonMatchingNextId: defaultEdge?.nextId ?? step.nonMatchingNextId,
        };
        setStep(newStep);
      }
    },
  );

  const onEnterNodeSelectMode = useHandler(() => {
    handleEnterNodeSelectMode({
      steps,
      setSelectionMode: selectionMode.setMode,
      setStep,
      step,
    });
  });

  const onEnterSubtreeCopyMode = useHandler(() => {
    handleEnterSubtreeCopyMode({
      steps,
      setSelectionMode: selectionMode.setMode,
      step,
      setAutomation,
    });
  });

  const onExitNodeSelectMode = useHandler(() => {
    selectionMode.setMode({ type: "normal" });
  });

  const excludeCharacteristics = useMemo(() => {
    const excludeCharacteristics = new Set<SegmentableCheckoutCharacteristic>(
      [],
    );

    // Hide old DELIVERY_LOCATION unless it's already selected
    if (
      step.segment?.segmentableCharacteristic !==
      SegmentableCheckoutCharacteristic.DELIVERY_LOCATION
    ) {
      excludeCharacteristics.add(
        SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
      );
    }

    if (
      (!team.settings.predictiveConversion?.enabled ||
        !team.settings.predictiveConversion.activeCheckoutHazardModel
          ?.endpointName) &&
      step.segment?.segmentableCharacteristic !==
        SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY
    ) {
      excludeCharacteristics.add(
        SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY,
      );
    }

    // Exclude forbidden segments when subscriptions mode is enabled
    if (team.settings.checkoutOptimization?.subscriptionsModeEnabled) {
      for (const characteristic of SUBSCRIPTIONS_MODE_FORBIDDEN_SEGMENTS) {
        excludeCharacteristics.add(characteristic);
      }
    }

    return excludeCharacteristics;
  }, [team, step.segment?.segmentableCharacteristic]);

  if (step.type !== StepType.RCO_SEGMENT) {
    return null;
  }

  return (
    <Flex align="flex-start" dir="column" gap="xl" pb="8xl">
      <Flex justify="space-between" w="full">
        <Flex align="center">
          <Text fontSize="md" fontWeight="semibold">
            Segment
          </Text>
        </Flex>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={XIcon}
          onClick={onClose}
          size="sm"
        />
      </Flex>
      {!readonly && selectionMode.mode.type === "normal" && (
        <SegmentCharacteristicSelector
          excludeCharacteristics={excludeCharacteristics}
          onCharacteristicSelected={handleCharacteristicSelected}
          selectedCharacteristic={step.segment?.segmentableCharacteristic}
          step={step}
        />
      )}
      <InspectorContent
        readonly={readonly}
        selectionMode={selectionMode.mode}
        setAutomation={setAutomation}
        setStep={setStep}
        step={step}
      />
      {!readonly && (
        <ClickToCopyExistingSegment
          onEnterNodeSelectMode={onEnterNodeSelectMode}
          onEnterSubtreeCopyMode={onEnterSubtreeCopyMode}
          onExitNodeSelectMode={onExitNodeSelectMode}
          selectionMode={selectionMode.mode}
          step={step}
          steps={steps}
        />
      )}
    </Flex>
  );
}

const InspectorContent = memo(function InspectorContent({
  step,
  setStep,
  readonly,
  selectionMode,
  setAutomation,
}: {
  step: PartialWithRequiredType<RcoSegmentStep>;
  setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  readonly: boolean | undefined;
  selectionMode: NodeSelectionMode;
  setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
}) {
  if (
    step.type !== StepType.RCO_SEGMENT ||
    !step.segment?.segmentableCharacteristic ||
    selectionMode.type !== "normal"
  ) {
    return null;
  }

  switch (step.segment.segmentableCharacteristic) {
    case SegmentableCheckoutCharacteristic.ORDER_COUNT:
      return readonly ? (
        <CustomerOrderCountInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableCustomerOrderCountInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE:
      return readonly ? (
        <HistoricalOrderValueInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableHistoricalOrderValueInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES:
      return readonly ? (
        <DeliveryDistanceMilesInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableDeliveryDistanceMilesInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION:
      return readonly ? (
        <DeliveryLocationInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableDeliveryLocationInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026:
      if (!step.segment) {
        return null;
      }
      return readonly ? (
        <DeliveryLocationNaryInspectorContent
          segment={step.segment as DeliveryLocationNarySegment}
        />
      ) : (
        <EditableDeliveryLocationNaryInspectorContent
          setAutomation={setAutomation}
          setStep={setStep}
          step={step}
        />
      );
    case SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT:
      return readonly ? (
        <CrossBorderShipmentInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableCrossBorderShipmentInspectorContent
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT:
      return readonly ? (
        <PoBoxShipmentInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditablePoBoxShipmentInspectorContent
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.CUSTOMER_TAGS:
      return readonly ? (
        <CustomerTagsInspectorContent step={rcoSegmentStepSchema.parse(step)} />
      ) : (
        <EditableCustomerTagsInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_TAGS:
      return readonly ? (
        <ProductTagsInspectorContent step={rcoSegmentStepSchema.parse(step)} />
      ) : (
        <EditableProductTagsInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_VENDOR:
      return readonly ? (
        <ProductVendorInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableProductVendorInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_SKU:
      return readonly ? (
        <ProductSkuInspectorContent step={rcoSegmentStepSchema.parse(step)} />
      ) : (
        <EditableProductSkuInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.ITEM_QUANTITY:
      return readonly ? (
        <ItemQuantityInspectorContent step={rcoSegmentStepSchema.parse(step)} />
      ) : (
        <EditableItemQuantityInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.ORIGIN_LOCATION:
      return readonly ? (
        <OriginLocationInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableOriginLocationInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE:
      return readonly ? (
        <CustomerReturnRateInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableCustomerReturnRateInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.TIME_OF_DAY:
      return readonly ? (
        <TimeOfDayInspectorContent step={rcoSegmentStepSchema.parse(step)} />
      ) : (
        <EditableTimeOfDayInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.DATETIME_RANGE:
      return readonly ? (
        <DatetimeRangeInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableDatetimeRangeInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY:
      return readonly ? (
        <CheckoutProbabilityInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableCheckoutProbabilityInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS:
      return readonly ? (
        <ProductCollectionsInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableProductCollectionsInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES:
      return readonly ? (
        <LineItemPropertiesInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableLineItemPropertiesInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS:
      return readonly ? (
        <ProductMetafieldsInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableProductMetafieldsInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.CART_VALUE:
      return readonly ? (
        <CartValueInspectorContent step={rcoSegmentStepSchema.parse(step)} />
      ) : (
        <EditableCartValueInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT:
      return readonly ? (
        <ShipmentWeightInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableShipmentWeightInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    case SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES:
      return readonly ? (
        <AddressPropertiesInspectorContent
          step={rcoSegmentStepSchema.parse(step)}
        />
      ) : (
        <EditableAddressPropertiesInspectorContent
          setStep={setStep}
          step={rcoSegmentStepSchemaOptionalNextIds.parse(step)}
        />
      );
    default:
      assertNever(step.segment.segmentableCharacteristic);
  }
});

const SegmentCharacteristicSelector = memo(
  function SegmentCharacteristicSelector({
    onCharacteristicSelected,
    selectedCharacteristic,
    step,
    excludeCharacteristics,
  }: {
    selectedCharacteristic: SegmentableCheckoutCharacteristic | undefined;
    onCharacteristicSelected: ({
      characteristic,
      step,
    }: {
      characteristic: SegmentableCheckoutCharacteristic;
      step: PartialWithRequiredType<RcoSegmentStep>;
    }) => void;
    step: PartialWithRequiredType<RcoSegmentStep>;
    excludeCharacteristics?: Set<SegmentableCheckoutCharacteristic>;
  }) {
    const characteristicOptions: TextRedoListItem<SegmentableCheckoutCharacteristic>[] =
      useMemo(() => {
        return Object.values(SegmentableCheckoutCharacteristic)
          .filter(
            (characteristic) => !excludeCharacteristics?.has(characteristic),
          )
          .map((characteristic) => ({
            type: "text" as const,
            id: characteristic,
            value: characteristic,
            text: segmentableCharacteristicLabel[characteristic],
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      }, [excludeCharacteristics]);

    const selectedItem = useMemo(() => {
      return characteristicOptions.find(
        (option) => option.value === selectedCharacteristic,
      );
    }, [characteristicOptions, selectedCharacteristic]);

    return (
      <RedoSingleSelectDropdownInput<SegmentableCheckoutCharacteristic>
        fitToAnchor={false}
        label="Segment by"
        options={characteristicOptions}
        optionSelected={({ item }) => {
          onCharacteristicSelected({ characteristic: item.value, step });
        }}
        placeholder="Select a segment characteristic"
        selectedItem={selectedItem}
      />
    );
  },
);

function handleEnterNodeSelectMode({
  steps,
  setSelectionMode,
  setStep,
  step,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  setSelectionMode: (selectionMode: NodeSelectionMode) => void;
  setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  step: PartialWithRequiredType<RcoSegmentStep>;
}) {
  if (step.type !== StepType.RCO_SEGMENT) {
    return;
  }

  // Enter segment-copy mode with the current step as source
  setSelectionMode({
    type: "node-copy",
    onNodeSelect: (nodeId: string) => {
      // Find the node that was clicked
      const sourceNode = steps.find((s) => s.id === nodeId);
      if (sourceNode?.type === StepType.RCO_SEGMENT) {
        // Copy the segment configuration from the source to the current node
        const newStep: PartialWithRequiredType<RcoSegmentStep> = {
          ...step,
          segment: sourceNode.segment,
        };
        setStep(newStep);
        toast("Successfully copied segment configuration.", {
          variant: "success",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
      } else {
        toast("Please select a segment node to copy.", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
        return;
      }

      // Return to normal mode
      setSelectionMode({ type: "normal" });
    },
  });
}

function handleEnterSubtreeCopyMode({
  steps,
  setSelectionMode,
  step,
  setAutomation,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  setSelectionMode: (selectionMode: NodeSelectionMode) => void;
  step: PartialWithRequiredType<RcoSegmentStep>;
  setAutomation:
    | React.Dispatch<React.SetStateAction<FlowInProgress | null>>
    | undefined;
}) {
  if (step.type !== StepType.RCO_SEGMENT) {
    return;
  }

  if (!setAutomation) {
    throw new Error("setAutomation is required");
  }

  const onNodeSelect = async (nodeId: string) => {
    // Find the node that was clicked
    const sourceNode = steps.find((s) => s.id === nodeId);
    if (sourceNode?.type === StepType.RCO_SEGMENT) {
      try {
        // Copy the subtree in frontend
        const result = copySubtree({
          steps,
          sourceNodeId: nodeId,
          targetNodeId: step.id,
        });
        if (!result.success) {
          toast(result.errorMessage, {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 5 }),
          });
          return;
        }

        setAutomation((prev) => {
          if (!prev) {
            return prev;
          }

          return { ...prev, steps: result.newSteps };
        });

        toast("Successfully copied segment subtree.", {
          variant: "success",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
      } catch (error) {
        console.error("Failed to copy segment subtree.", error);
        toast("Failed to copy segment subtree.", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 5 }),
        });
      }
    } else {
      toast("Please select a segment node to copy.", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 5 }),
      });
      return;
    }

    // Return to normal mode
    setSelectionMode({ type: "normal" });
  };

  // Enter subtree-copy mode
  setSelectionMode({ type: "node-copy-subtree", onNodeSelect });
}

const ClickToCopyExistingSegment = memo(function ClickToCopyExistingSegment({
  steps,
  step,
  onEnterNodeSelectMode,
  onEnterSubtreeCopyMode,
  onExitNodeSelectMode,
  selectionMode,
}: {
  steps: PartialWithRequiredType<CheckoutOptimizationStep>[];
  step: PartialWithRequiredType<CheckoutOptimizationStep>;
  onEnterNodeSelectMode: () => void;
  onEnterSubtreeCopyMode: () => void;
  onExitNodeSelectMode: () => void;
  selectionMode: NodeSelectionMode;
}) {
  const hasExistingSegment = useMemo(() => {
    return steps.some(
      (s) => s.type === StepType.RCO_SEGMENT && s.id !== step.id,
    );
  }, [step, steps]);

  if (!hasExistingSegment) {
    return null;
  }

  return selectionMode.type === "normal" ? (
    <>
      <Flex
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        p="none"
        w="full"
      />
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          or
        </Text>
        <RedoButton
          hierarchy="secondary"
          onClick={onEnterNodeSelectMode}
          size="xs"
          text="copy existing segment configuration"
        />
      </Flex>
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          or
        </Text>
        <RedoButton
          hierarchy="secondary"
          onClick={onEnterSubtreeCopyMode}
          size="xs"
          text="copy existing segment and subtree"
        />
      </Flex>
    </>
  ) : selectionMode.type === "node-copy-subtree" ? (
    <>
      <Text fontSize="xs" textColor="secondary">
        Click a segment node in the tree to copy it and its subtree
      </Text>
      <RedoButton
        hierarchy="secondary"
        onClick={onExitNodeSelectMode}
        size="xs"
        text="Cancel"
      />
    </>
  ) : selectionMode.type === "node-copy" ? (
    <>
      <Text fontSize="xs" textColor="secondary">
        Click a segment node in the tree to copy its configuration
      </Text>
      <RedoButton
        hierarchy="secondary"
        onClick={onExitNodeSelectMode}
        size="xs"
        text="Cancel"
      />
    </>
  ) : (
    assertNever(selectionMode)
  );
});

export const rcoSegmentStepType: FrontendStepType<
  PartialWithRequiredType<RcoSegmentStep>
> = {
  type: "custom",
  // TODO: validate each segment type
  valid: (step) => validateSegment({ step }),
  selectable: (_) => true,
  customInspector: RcoSegmentInspector,
};

function validateSegment({
  step,
}: {
  step: PartialWithRequiredType<RcoSegmentStep>;
}): boolean {
  if (!step.segment || !step.segment.segmentableCharacteristic) {
    return false;
  }

  if (narySegmentCharacteristics.has(step.segment.segmentableCharacteristic)) {
    if (!hasAllEdgesConnected(step)) {
      return false;
    }
  } else if (!step.matchingNextId || !step.nonMatchingNextId) {
    return false;
  }

  switch (step.segment.segmentableCharacteristic) {
    case SegmentableCheckoutCharacteristic.DATETIME_RANGE: {
      if (!step.segment.startDateTime || !step.segment.endDateTime) {
        return false;
      }
      const startPlainDateTime = Temporal.PlainDateTime.from(
        step.segment.startDateTime,
      );
      const endPlainDateTime = Temporal.PlainDateTime.from(
        step.segment.endDateTime,
      );
      return (
        Temporal.PlainDateTime.compare(startPlainDateTime, endPlainDateTime) < 0
      );
    }
    case SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY:
      return CheckoutProbabilitySegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE:
      return CustomerReturnRateSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CUSTOMER_TAGS:
      return CustomerTagsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES:
      return DeliveryDistanceMilesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION:
      return DeliveryLocationSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ITEM_QUANTITY:
      return ItemQuantitySegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES:
      return LineItemPropertiesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ORDER_COUNT:
      return OrderCountSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE:
      return HistoricalOrderValueSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ORIGIN_LOCATION:
      return OriginLocationSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS:
      return ProductCollectionsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_SKU:
      return ProductSkuSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_TAGS:
      return ProductTagsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_VENDOR:
      return ProductVendorSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.TIME_OF_DAY:
      return TimeOfDaySegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS:
      return ProductMetafieldsSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CART_VALUE:
      return CartValueSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT:
      return ShipmentWeightSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT:
      return CrossBorderShipmentSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT:
      return PoBoxShipmentSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES:
      return AddressPropertiesSegmentSchema.safeParse(step.segment).success;
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_V02262026:
      return isValidDeliveryLocationNarySegment(step.segment);
    default:
      assertNever(step.segment.segmentableCharacteristic);
  }
}

function hasAllEdgesConnected(
  step: PartialWithRequiredType<RcoSegmentStep>,
): boolean {
  if (!step.edges || step.edges.length === 0) {
    return false;
  }
  return step.edges.every((edge) => edge.nextId && edge.nextId !== "");
}

function isValidDeliveryLocationNarySegment(segment: unknown): boolean {
  const result = DeliveryLocationNarySegmentSchema.safeParse(segment);
  if (!result.success) {
    return false;
  }
  return (
    result.data.conditions.length > 0 &&
    result.data.conditions.every((c) => c.locationRules.length > 0)
  );
}
